import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Alert, Button, Input } from '@/components/ui';
import { useAnnouncer } from '@/lib/a11y';
import { joinReopenAlerts } from '@/lib/api/meta';
import { toApiError, type ApiError } from '@/lib/api/client';
import { normalisePhone, otpFormSchema, type OtpFormValues } from '@/lib/validators';

/**
 * "Tell me when you are open" — the only thing left to offer a visitor who
 * arrives while the shop is shut.
 *
 * Without it a closed shop is a dead end: someone who needed printing today
 * reads "back Monday", leaves, and is gone. A number costs them one field and
 * gives them a reason not to go and find somebody else.
 *
 * NO OTP HERE, deliberately. Nobody is proving anything and nothing is being
 * spent — the worst case is one SMS to a number somebody mistyped, which is a
 * smaller harm than making a closed shop's last call to action a two-step
 * verification. The server rate-limits by address for the same reason.
 *
 * Asking twice is a success. A customer who taps again because nothing
 * visibly happened has done nothing wrong, and the server keys the list on the
 * number so they are still texted once.
 */
export function ReopenAlertForm() {
  const { announce } = useAnnouncer();
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const form = useForm<OtpFormValues>({
    resolver: zodResolver(otpFormSchema),
    defaultValues: { phone: '' },
    mode: 'onSubmit',
  });

  async function submit(values: OtpFormValues) {
    setError(null);
    try {
      await joinReopenAlerts(normalisePhone(values.phone));
      setJoined(true);
      announce('We will text you when we are taking orders again.');
    } catch (thrown) {
      const apiError = toApiError(thrown);
      // The shop reopened between the page loading and this submit. Telling
      // them to wait would be nonsense; telling them to go ahead is the truth.
      if (apiError.code === 'shop_is_open') {
        setJoined(true);
        announce('We are taking orders again. Refresh to start one.');
        return;
      }
      const fieldError = apiError.fieldErrors['phone'];
      if (fieldError) form.setError('phone', { message: fieldError });
      else setError(apiError);
    }
  }

  if (joined) {
    return (
      <div className="mt-3 rounded-control border border-white/20 bg-white/5 p-3">
        <p className="text-sm font-semibold text-brand">We have your number</p>
        <p className="mt-1 text-sm text-ink-inverse/80">
          We will text you once, as soon as we are taking orders again.
        </p>
      </div>
    );
  }

  return (
    <form
      noValidate
      onSubmit={(event) => {
        void form.handleSubmit(submit)(event);
      }}
      className="mt-3 flex flex-col gap-2"
    >
      <p className="text-sm text-ink-inverse/80">
        Leave your number and we will text you when we are back.
      </p>

      {error ? (
        <Alert severity="danger" title="We could not save that" live>
          <p>{error.message}</p>
        </Alert>
      ) : null}

      <Input
        {...form.register('phone')}
        label="Mobile number"
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        prefix="+91"
        placeholder="9876543210"
        maxLength={13}
        required
        error={form.formState.errors.phone?.message}
      />

      <Button type="submit" loading={form.formState.isSubmitting} loadingText="Saving…">
        Text me when you open
      </Button>
    </form>
  );
}
